import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { WhatsAppCTA } from '../components/ui/whatsapp-cta';
import { renderTipTapToHTML } from '../utils/tiptapRenderer';
import { StructuredContentRenderer } from '../components/blog-v1/StructuredContentRenderer';
import { BlogTableOfContents } from '../components/blog-v1/BlogTableOfContents';
import { getTitlePlainText, getTitleHtml } from '../utils/titleFormat';
import { ensureAbsoluteUrl, truncateText } from '../utils/socialMeta';
import { API_BASE } from '../config/api';

export const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [recommendedPrograms, setRecommendedPrograms] = useState([]);
  const [programCarouselIndex, setProgramCarouselIndex] = useState(0);
  const [universities, setUniversities] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const backendUrl = API_BASE;

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        setLoading(true);
        
        const blogResponse = await fetch(`${backendUrl}/api/public/blogs/${slug}`);
        if (!blogResponse.ok) {
          if (blogResponse.status === 404) {
            throw new Error('Blog not found');
          }
          throw new Error('Failed to fetch blog details');
        }
        const blogData = await blogResponse.json();
        
        // Fetch all blogs for related and featured articles
        const allBlogsResponse = await fetch(`${backendUrl}/api/public/blogs`);
        if (allBlogsResponse.ok) {
          const allBlogsData = await allBlogsResponse.json();
          
          // Filter related articles by category, exclude current blog
          const related = (allBlogsData.blogs || [])
            .filter(b => b.category === blogData.blog.category && b.slug !== slug)
            .sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0))
            .slice(0, 4);
          
          // Featured articles for sidebar (same category first, then others)
          const featured = (allBlogsData.blogs || [])
            .filter(b => b.is_featured && b.slug !== slug)
            .slice(0, 3);
          
          setRelatedArticles(related);
          setFeaturedArticles(featured);
        }

        // Fetch programs and universities for recommended section
        const [programsResponse, universitiesResponse] = await Promise.all([
          fetch(`${backendUrl}/api/public/programs`),
          fetch(`${backendUrl}/api/public/universities`)
        ]);
        
        if (programsResponse.ok && universitiesResponse.ok) {
          const programsData = await programsResponse.json();
          const universitiesData = await universitiesResponse.json();
          
          // Create university lookup map
          const universityMap = {};
          (universitiesData.universities || []).forEach(uni => {
            universityMap[uni.id] = uni.name;
          });
          setUniversities(universityMap);
          
          const programs = (programsData.programs || []).slice(0, 6).map(p => ({
            ...p,
            university_name: universityMap[p.university_id] || 'Unknown University'
          }));
          setRecommendedPrograms(programs);
        }
        
        setBlog(blogData.blog);
        
        // Set document title and meta description for SEO
        document.title = `${getTitlePlainText(blogData.blog.title)} | Unisave`;
        
        // Set meta description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.name = 'description';
          document.head.appendChild(metaDescription);
        }
        metaDescription.content = blogData.blog.excerpt || blogData.blog.content?.substring(0, 160) || 'Read this comprehensive guide on Unisave';
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlogData();
    }
  }, [slug, backendUrl]);

  const handleConsultClick = () => {
    window.open('https://wa.me/60123456789?text=Hi, I would like to speak with an advisor', '_blank');
  };

  const handleProgramNext = () => {
    if (programCarouselIndex < recommendedPrograms.length - 3) {
      setProgramCarouselIndex(prev => prev + 1);
    }
  };

  const handleProgramPrev = () => {
    if (programCarouselIndex > 0) {
      setProgramCarouselIndex(prev => prev - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#F97316] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-[#4B5563]">Loading guide...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111827] mb-4">Guide Not Found</h1>
            <p className="text-[#4B5563] mb-6">{error}</p>
            <button 
              onClick={() => navigate('/blogs')}
              className="px-6 py-3 bg-[#F97316] hover:bg-[#fb7e2e] text-white font-semibold rounded-lg transition-colors"
            >
              Browse All Guides
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!blog) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col overflow-x-hidden">
      {/* SEO Meta Tags, Open Graph, Twitter, Structured Data, Canonical */}
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{blog.meta_title || getTitlePlainText(blog.title)} | Unisave</title>
        <meta name="description" content={blog.meta_description || blog.excerpt || ''} />
        {blog.tags && blog.tags.length > 0 && (
          <meta name="keywords" content={blog.tags.join(', ')} />
        )}
        
        {/* Canonical URL */}
        <link rel="canonical" href={`https://www.unisave.my/blogs/${blog.slug}`} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={blog.meta_title || getTitlePlainText(blog.title)} />
        <meta property="og:description" content={truncateText(blog.meta_description || blog.excerpt || '', 200)} />
        <meta property="og:image" content={ensureAbsoluteUrl(blog.featured_image)} />
        <meta property="og:url" content={`https://www.unisave.my/blogs/${blog.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Unisave" />
        {blog.created_at && <meta property="article:published_time" content={blog.created_at} />}
        {blog.updated_at && <meta property="article:modified_time" content={blog.updated_at} />}
        {blog.tags && blog.tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.meta_title || getTitlePlainText(blog.title)} />
        <meta name="twitter:description" content={truncateText(blog.meta_description || blog.excerpt || '', 200)} />
        <meta name="twitter:image" content={ensureAbsoluteUrl(blog.featured_image)} />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": getTitlePlainText(blog.title),
            "image": blog.featured_image ? [blog.featured_image] : [],
            "datePublished": blog.created_at || new Date().toISOString(),
            "dateModified": blog.updated_at || blog.created_at || new Date().toISOString(),
            "author": {
              "@type": "Organization",
              "name": "Unisave"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Unisave",
              "logo": {
                "@type": "ImageObject",
                "url": "https://unisave.my/logo.png"
              }
            },
            "description": blog.excerpt || ''
          })}
        </script>
      </Helmet>

      <Header />
      
      <main className="flex-1 w-full px-6 md:px-8 lg:px-12 py-8 md:py-12" style={{ backgroundColor: '#FFFDFB' }}>
        <div className="w-full max-w-[1200px] mx-auto">
          
          {/* BREADCRUMB */}
          <nav className="mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <a href="/" className="text-[#6B7280] hover:text-[#F97316] transition-colors">Home</a>
              </li>
              <li className="text-[#9CA3AF]">/</li>
              <li>
                <a href="/blogs" className="text-[#6B7280] hover:text-[#F97316] transition-colors">Guides</a>
              </li>
              <li className="text-[#9CA3AF]">/</li>
              <li className="text-[#111827] font-semibold truncate max-w-[200px] md:max-w-none">{getTitlePlainText(blog.title)}</li>
            </ol>
          </nav>

          {/* 2-COLUMN LAYOUT */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            
            {/* LEFT COLUMN - ARTICLE (70%) */}
            <article className="lg:col-span-8 w-full max-w-full">
              {/* ARTICLE HEADER */}
              <header className="mb-8">
                {/* Category and Featured Badge */}
                <div className="flex items-center gap-3 mb-5">
                  <span className="inline-block px-4 py-1.5 bg-[#E0F2FE] text-[#0369A1] text-sm font-semibold rounded-full">
                    {blog.category || 'General'}
                  </span>
                  {blog.is_featured && (
                    <span className="inline-block px-4 py-1.5 bg-gradient-to-r from-[#059669] to-[#10b981] text-white text-sm font-bold rounded-full flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Featured
                    </span>
                  )}
                </div>
                
                {/* Blog Title - H1 for SEO with rich text formatting */}
                <h1 
                  className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#111827] mb-5 leading-tight break-words"
                  dangerouslySetInnerHTML={{ __html: getTitleHtml(blog.title) || getTitlePlainText(blog.title) }}
                />
                
                {/* Published Date */}
                <p className="text-[#6B7280] text-base flex items-center gap-2 mb-8">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {new Date(blog.published_date || blog.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                
                {/* Featured Image */}
                {blog.featured_image && (
                  <div className="w-full rounded-2xl overflow-hidden shadow-xl mb-8">
                    <img 
                      src={blog.featured_image} 
                      alt={getTitlePlainText(blog.title)}
                      loading="lazy"
                      className="w-full object-cover"
                      style={{ maxHeight: '240px' }}
                      onLoad={(e) => {
                        if (window.innerWidth >= 1024) {
                          e.target.style.maxHeight = '400px';
                        }
                      }}
                    />
                  </div>
                )}
              </header>

              {/* ARTICLE CONTENT - SEO-CRITICAL */}
              <section className="mb-10">
                <div 
                  className="prose prose-lg max-w-none prose-headings:text-[#111827] prose-p:text-[#4B5563] prose-a:text-[#1E40AF] prose-strong:text-[#111827]"
                  style={{
                    fontSize: '1.0625rem',
                    lineHeight: '1.8',
                    color: '#4B5563'
                  }}
                >
                  {/* Render structured content sections if available, otherwise fallback to legacy content */}
                  {blog.content_sections && blog.content_sections.length > 0 ? (
                    <StructuredContentRenderer 
                      contentSections={blog.content_sections}
                      legacyContent={blog.content}
                    />
                  ) : blog.content && typeof blog.content === 'object' && blog.content.type === 'doc' && Array.isArray(blog.content.content) ? (
                    <div 
                      dangerouslySetInnerHTML={{ __html: renderTipTapToHTML(blog.content) }}
                      className="blog-content"
                    />
                  ) : (
                    <p className="text-[#4B5563] leading-relaxed">
                      {blog.excerpt || 'Content is being updated. Please check back soon or contact our advisors for more information.'}
                    </p>
                  )}
                </div>
              </section>

              {/* RECOMMENDED PROGRAMS */}
              {recommendedPrograms.length > 0 && (
                <section className="py-8 border-t border-[#E5E7EB]">
                  <h2 className="text-xl md:text-2xl font-bold text-[#111827] mb-1">Recommended Programs</h2>
                  <p className="text-[#6B7280] text-sm mb-5">Based on this guide, you might be interested in these programs</p>
                  
                  {/* Desktop: 3-Column Carousel with Arrows */}
                  <div className="hidden md:block relative">
                    {/* Left Arrow */}
                    {programCarouselIndex > 0 && (
                      <button
                        onClick={handleProgramPrev}
                        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white border-2 border-[#E5E7EB] hover:border-[#F97316] hover:bg-[#F97316] hover:text-white rounded-full flex items-center justify-center shadow-lg transition-all"
                        aria-label="Previous programs"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                    )}

                    {/* Carousel Container */}
                    <div className="overflow-hidden">
                      <div 
                        className="flex gap-5 transition-transform duration-500 ease-in-out"
                        style={{ transform: `translateX(-${programCarouselIndex * (100 / 3 + 1.67)}%)` }}
                      >
                        {recommendedPrograms.map(program => (
                          <div
                            key={program.id}
                            onClick={() => navigate(`/programs/${program.id}`)}
                            className="bg-white border-2 border-[#E5E7EB] rounded-xl p-5 hover:border-[#F97316] hover:shadow-lg transition-all cursor-pointer flex flex-col flex-shrink-0"
                            style={{ width: 'calc(33.333% - 14px)' }}
                          >
                            <h3 className="font-bold text-[#111827] mb-2 line-clamp-2 text-base leading-tight">
                              {program.program_name}
                            </h3>
                            <p className="text-[#4B5563] text-sm mb-4 font-semibold">
                              {program.university_name}
                            </p>
                            
                            <div className="space-y-2 mb-4">
                              {program.estimated_fee_local && (
                                <div>
                                  <p className="text-xs text-[#6B7280]">Local Student Fee</p>
                                  <p className="text-sm font-bold text-[#111827]">
                                    RM {program.estimated_fee_local.toLocaleString()}
                                  </p>
                                </div>
                              )}
                              
                              {program.estimated_fee_international && (
                                <div>
                                  <p className="text-xs text-[#6B7280]">International Student Fee</p>
                                  <p className="text-sm font-bold text-[#111827]">
                                    RM {program.estimated_fee_international.toLocaleString()}
                                  </p>
                                </div>
                              )}
                              
                              {program.cashback_amount && (
                                <div className="pt-2 border-t border-[#E5E7EB]">
                                  <p className="text-sm font-bold text-[#F97316]">
                                    💰 Cashback up to RM {program.cashback_amount.toLocaleString()}
                                  </p>
                                </div>
                              )}
                            </div>
                            
                            <button className="w-full mt-auto px-4 py-2.5 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-lg text-white font-semibold rounded-lg transition-all text-sm">
                              Explore Program →
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right Arrow */}
                    {programCarouselIndex < recommendedPrograms.length - 3 && (
                      <button
                        onClick={handleProgramNext}
                        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white border-2 border-[#E5E7EB] hover:border-[#F97316] hover:bg-[#F97316] hover:text-white rounded-full flex items-center justify-center shadow-lg transition-all"
                        aria-label="Next programs"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Mobile: 1-Column Swipeable Carousel */}
                  <div className="md:hidden relative w-full overflow-x-hidden px-4">
                    <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                      {recommendedPrograms.map(program => (
                        <div
                          key={program.id}
                          onClick={() => navigate(`/programs/${program.id}`)}
                          className="bg-white border-2 border-[#E5E7EB] rounded-xl p-4 hover:border-[#F97316] hover:shadow-lg transition-all cursor-pointer flex flex-col flex-shrink-0 snap-center"
                          style={{ width: 'calc(100vw - 3rem)', maxWidth: '320px' }}
                        >
                          <h3 className="font-bold text-[#111827] mb-2 line-clamp-2 leading-tight">
                            {program.program_name}
                          </h3>
                          <p className="text-[#4B5563] text-sm mb-3 font-semibold">
                            {program.university_name}
                          </p>
                          
                          <div className="space-y-2 mb-3">
                            {program.estimated_fee_local && (
                              <div>
                                <p className="text-xs text-[#6B7280]">Local Student Fee</p>
                                <p className="text-sm font-bold text-[#111827]">
                                  RM {program.estimated_fee_local.toLocaleString()}
                                </p>
                              </div>
                            )}
                            
                            {program.estimated_fee_international && (
                              <div>
                                <p className="text-xs text-[#6B7280]">International Student Fee</p>
                                <p className="text-sm font-bold text-[#111827]">
                                  RM {program.estimated_fee_international.toLocaleString()}
                                </p>
                              </div>
                            )}
                            
                            {program.cashback_amount && (
                              <div className="pt-2 border-t border-[#E5E7EB]">
                                <p className="text-xs font-bold text-[#F97316]">
                                  💰 Cashback up to RM {program.cashback_amount.toLocaleString()}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          <button className="w-full mt-auto px-3 py-2 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] text-white font-semibold rounded-lg text-xs">
                            Explore Program →
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {/* RELATED ARTICLES - 3 Column Grid + Horizontal Scroll */}
              {relatedArticles.length > 0 && (
                <section className="py-8 border-t border-[#E5E7EB]">
                  <h2 className="text-xl md:text-2xl font-bold text-[#111827] mb-5">Related Articles</h2>
                  
                  {/* Desktop: 3-Column Grid with horizontal scroll for overflow */}
                  <div className="hidden md:block">
                    <div className="grid grid-cols-3 gap-6 overflow-x-auto">
                      {relatedArticles.map(article => (
                        <div
                          key={article.id}
                          onClick={() => navigate(`/blogs/${article.slug || article.id}`)}
                          data-testid={`related-article-${article.slug}`}
                          className="bg-white rounded-xl overflow-hidden border-2 border-[#E5E7EB] hover:border-[#F97316] hover:shadow-lg transition-all cursor-pointer flex flex-col"
                        >
                          {/* Featured Image - Smaller */}
                          <div className="relative w-full pt-[56.25%] bg-[#F8FAFC]">
                            {article.featured_image ? (
                              <img 
                                src={article.featured_image} 
                                alt={getTitlePlainText(article.title)}
                                loading="lazy"
                                className="absolute top-0 left-0 w-full h-full object-cover"
                              />
                            ) : (
                              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                                <svg className="w-10 h-10 text-[#9CA3AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                            )}
                            {article.is_featured && (
                              <div className="absolute top-2 right-2 bg-gradient-to-r from-[#059669] to-[#10b981] text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                                ⭐
                              </div>
                            )}
                          </div>
                          
                          {/* Card Content - Compact */}
                          <div className="p-4 flex flex-col flex-1">
                            <span className="inline-block px-2 py-1 bg-[#E0F2FE] text-[#0369A1] text-xs font-semibold rounded-full mb-2 self-start">
                              {article.category || 'General'}
                            </span>
                            
                            <h3 className="text-base font-bold text-[#111827] mb-2 line-clamp-2 leading-snug">
                              {getTitlePlainText(article.title)}
                            </h3>
                            
                            <p className="text-[#6B7280] text-sm mb-3 line-clamp-2 flex-1 leading-relaxed">
                              {article.excerpt || 'Read this guide...'}
                            </p>
                            
                            <a 
                              href={`/blogs/${article.slug || article.id}`}
                              className="text-[#F97316] hover:text-[#fb7e2e] font-semibold text-xs flex items-center gap-1 mt-auto"
                            >
                              Read More
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mobile: Horizontal Carousel */}
                  <div className="md:hidden relative w-full overflow-x-hidden px-4">
                    <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                      {relatedArticles.map(article => (
                        <div
                          key={article.id}
                          onClick={() => navigate(`/blogs/${article.slug || article.id}`)}
                          data-testid={`related-article-mobile-${article.slug}`}
                          className="bg-white rounded-xl overflow-hidden border-2 border-[#E5E7EB] hover:border-[#F97316] hover:shadow-lg transition-all cursor-pointer flex flex-col flex-shrink-0 snap-center"
                          style={{ width: 'calc(100vw - 3rem)', maxWidth: '320px' }}
                        >
                          {/* Featured Image */}
                          <div className="relative w-full pt-[56.25%] bg-[#F8FAFC]">
                            {article.featured_image ? (
                              <img 
                                src={article.featured_image} 
                                alt={getTitlePlainText(article.title)}
                                loading="lazy"
                                className="absolute top-0 left-0 w-full h-full object-cover"
                              />
                            ) : (
                              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                                <svg className="w-12 h-12 text-[#9CA3AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                            )}
                            {article.is_featured && (
                              <div className="absolute top-3 right-3 bg-gradient-to-r from-[#059669] to-[#10b981] text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                                ⭐ Featured
                              </div>
                            )}
                          </div>
                          
                          {/* Card Content */}
                          <div className="p-4 flex flex-col flex-1">
                            <span className="inline-block px-3 py-1 bg-[#E0F2FE] text-[#0369A1] text-xs font-semibold rounded-full mb-3 self-start">
                              {article.category || 'General'}
                            </span>
                            
                            <h3 className="text-lg font-bold text-[#111827] mb-3 line-clamp-2">
                              {getTitlePlainText(article.title)}
                            </h3>
                            
                            <p className="text-[#6B7280] text-sm mb-4 line-clamp-2 flex-1">
                              {article.excerpt || 'Read this comprehensive guide to learn more...'}
                            </p>
                            
                            <a 
                              href={`/blogs/${article.slug || article.id}`}
                              className="text-[#F97316] hover:text-[#fb7e2e] font-semibold text-sm flex items-center gap-2 mt-auto"
                            >
                              Read More
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}
            </article>

            {/* RIGHT COLUMN - SIDEBAR (30%) - Sticky on Desktop */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 space-y-4">
                
                {/* TABLE OF CONTENTS */}
                {blog.content_sections && blog.content_sections.length > 0 && (
                  <BlogTableOfContents sections={blog.content_sections} />
                )}
                
                {/* AD BANNERS FROM BLOG DATA - Scaled Down */}
                <div className="space-y-3">
                  {blog.ad_banners && blog.ad_banners.length > 0 ? (
                    blog.ad_banners
                      .filter(banner => banner.image_url && banner.image_url.trim() !== '')
                      .map((banner, index) => (
                        <a
                          key={index}
                          href={banner.target_url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full rounded-xl overflow-hidden hover:opacity-90 transition-opacity cursor-pointer"
                          style={{ aspectRatio: '1/1' }}
                        >
                          <img 
                            src={banner.image_url} 
                            alt={`Advertisement ${index + 1}`}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        </a>
                      ))
                  ) : (
                    <>
                      <div className="w-full bg-gradient-to-br from-[#F8FAFC] to-white border-2 border-dashed border-[#E5E7EB] rounded-xl flex items-center justify-center" style={{ aspectRatio: '1/1' }}>
                        <div className="text-center text-[#9CA3AF]">
                          <svg className="w-10 h-10 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-xs font-medium">Square Ad</p>
                        </div>
                      </div>

                      <div className="w-full bg-gradient-to-br from-[#F8FAFC] to-white border-2 border-dashed border-[#E5E7EB] rounded-xl flex items-center justify-center" style={{ aspectRatio: '1/1' }}>
                        <div className="text-center text-[#9CA3AF]">
                          <svg className="w-10 h-10 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-xs font-medium">Square Ad</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* QUICK CONSULTATION CARD - Scaled Down */}
                <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 bg-[#1E40AF] rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#111827] text-sm">Need Guidance?</h3>
                      <p className="text-xs text-[#6B7280]">Talk to our advisors</p>
                    </div>
                  </div>
                  <p className="text-[#6B7280] text-xs mb-4 leading-relaxed">
                    Get personalized advice on your study abroad journey.
                  </p>
                  <div className="space-y-2">
                    <button
                      onClick={handleConsultClick}
                      data-testid="sidebar-consult-button"
                      className="w-full px-4 py-2.5 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Consult Now
                    </button>
                    <button
                      onClick={handleConsultClick}
                      data-testid="sidebar-whatsapp-button"
                      className="w-full px-4 py-2.5 bg-white border border-[#059669] text-[#059669] hover:bg-[#059669] hover:text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </button>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      
      <Footer />
      <WhatsAppCTA phone="60123456789" message={`Hi, I would like to discuss about: ${getTitlePlainText(blog.title)}`} />
    </div>
  );
};

export default BlogDetail;
